import { ref, reactive } from "vue";
import type { Ref } from "vue";
import {
  ShopListInput,
  DeliveryTimeListInput,
  DeliverableAreaDeliveryIdInput,
  GetShopQuery,
  GetShopDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
} from "@/generated/graphql";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import { ApolloQueryResult } from "@apollo/client/core";
import type {
  ListData,
  ListOptions,
  ShopData,
  ChainSelectArray,
  ShopSelectArray,
  DeliveryAreaFilterArray,
} from "../types";

/**
 * フィルタ関連の状態（ref/reactive）とデータ取得関数を提供する。
 * ハンドラ（onChangeChain 等）は useScheduledDeliveryFilterHandlers で提供する。
 */
export const useScheduledDeliveryFilterState = (params: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}) => {
  /**
   * チェーンリスト
   */
  const chainListItems = ref<ChainSelectArray>([]);
  const chainSelectValues = ref<{ chain_code: string; name: string } | null>({
    chain_code: "",
    name: "",
  });

  /**
   * 出荷元リスト
   */
  const shopListFromItems = ref<ShopSelectArray>([]);
  const shopSelectFromValues = ref<{ shop_code: string; name: string }>({
    shop_code: "",
    name: "",
  });

  /**
   * 配達先リスト
   */
  const shopListToItems = ref<ShopSelectArray>([]);
  const shopSelectToValues = ref<{ shop_code: string; name: string }>({
    shop_code: "",
    name: "",
  });

  /**
   * 店舗情報
   */
  const shopData = ref<ShopData | null | undefined>(null);
  const shopData_from_name = ref<string>("");
  const shopData_from_address = ref<string>("");
  const shopData_from_zip_code = ref<string>("");
  const shopData_to_name = ref<string>("");
  const shopData_to_address = ref<string>("");

  /**
   * エリアリスト
   */
  const areaListItems = ref<DeliveryAreaFilterArray>([]);

  /**
   * 店舗一覧 input
   */
  const shopListInput = reactive<ShopListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  /**
   * 配達時刻一覧 input
   */
  const deliveryTimeListInput = reactive<DeliveryTimeListInput>({
    area_code: [],
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    limit: 10000,
    offset: 0,
  });

  /**
   * 配達可能なエリア・便 input
   */
  const deliverableAreaDeliveryIdInput =
    reactive<DeliverableAreaDeliveryIdInput>({
      chain_code: "",
      customer_code: localStorageGetCustomerCode(),
      shop_code: "",
      delivered_zip_code: "",
    });

  /**
   * 店舗情報取得
   */
  const getShopData = async (shop_code: string) => {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      const shop_data: ApolloQueryResult<GetShopQuery> =
        await apolloClient.query<GetShopQuery>({
          query: GetShopDocument,
          variables: {
            chain_code: chainSelectValues.value!.chain_code!,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shop_code,
          },
        });

      return shop_data;
    } catch (err: unknown) {
      console.error(err);
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = err.message;
      } else {
        params.errMessage.value = "予期しないエラーが発生しました。";
      }
      return undefined;
    }
  };

  /**
   * エリア情報取得
   */
  const GetAreaData = async (shop_code: string) => {
    if (shop_code == "") {
      return;
    }

    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // エリアデータ取得処理
      const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
        await apolloClient.query<
          ListDeliveryAreaQuery,
          ListDeliveryAreaQueryVariables
        >({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: chainSelectValues.value!.chain_code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shop_code,
          },
          fetchPolicy: "no-cache",
        });
      areaListItems.value = listArea.data.listDeliveryArea;

      areaListItems.value?.forEach((value) => {
        if (value !== undefined && value !== null) {
          deliveryTimeListInput.area_code!.push(value.area_code);
        }
      });
    } catch (err: unknown) {
      console.error(err);
      params.errdialog.value = true;
      params.errMessage.value = "配達エリアが存在しません。";
      return;
    }
  };

  return {
    chainListItems,
    chainSelectValues,
    shopListFromItems,
    shopSelectFromValues,
    shopListToItems,
    shopSelectToValues,
    shopData,
    shopData_from_name,
    shopData_from_address,
    shopData_from_zip_code,
    shopData_to_name,
    shopData_to_address,
    areaListItems,
    shopListInput,
    deliveryTimeListInput,
    deliverableAreaDeliveryIdInput,
    getShopData,
    GetAreaData,
  };
};

/**
 * フィルタ選択ハンドラ（チェーン/出荷元/配達先の選択変更）を提供する。
 * テーブル状態（listOptions 等）に依存するため、テーブル composable の後に呼び出す。
 */
export const useScheduledDeliveryFilterHandlers = (params: {
  chainSelectValues: Ref<{ chain_code: string; name: string } | null>;
  shopSelectFromValues: Ref<{ shop_code: string; name: string }>;
  shopSelectToValues: Ref<{ shop_code: string; name: string }>;
  shopListFromItems: Ref<ShopSelectArray>;
  shopListToItems: Ref<ShopSelectArray>;
  shopData: Ref<ShopData | null | undefined>;
  shopData_from_name: Ref<string>;
  shopData_from_address: Ref<string>;
  shopData_from_zip_code: Ref<string>;
  shopData_to_name: Ref<string>;
  shopData_to_address: Ref<string>;
  shopListInput: ShopListInput;
  deliveryTimeListInput: DeliveryTimeListInput;
  deliverableAreaDeliveryIdInput: DeliverableAreaDeliveryIdInput;
  listOptions: Ref<ListOptions>;
  listDataTotalCount: Ref<number>;
  listDataArray: Ref<ListData[]>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  getShopData: (
    shop_code: string
  ) => Promise<ApolloQueryResult<GetShopQuery> | undefined>;
  GetAreaData: (shop_code: string) => Promise<void>;
}) => {
  /**
   * 「チェーン」選択ボックス @change
   */
  const onChangeChain = async (): Promise<void> => {
    try {
      if (!params.shopSelectFromValues.value) {
        params.shopSelectFromValues.value = { shop_code: "", name: "" };
      }
      if (!params.shopSelectToValues.value) {
        params.shopSelectToValues.value = { shop_code: "", name: "" };
      }

      params.shopSelectFromValues.value.shop_code = "";
      params.shopSelectFromValues.value.name = "";
      params.shopSelectToValues.value.shop_code = "";
      params.shopSelectToValues.value.name = "";
      params.shopListFromItems.value = [];
      params.shopListToItems.value = [];

      params.deliveryTimeListInput.chain_code = "";
      params.shopListInput.chain_code = "";

      //ページを1ページに戻す
      params.listOptions.value.page = 1;
      params.listDataTotalCount.value = 0;

      //チェーン名が空の場合、店舗、エリア名も空にする
      if (!params.chainSelectValues.value) {
        params.listDataArray.value = [];
        return;
      }

      //チェーンコードを設定
      params.shopListInput.chain_code =
        params.chainSelectValues.value!.chain_code;
      params.deliveryTimeListInput.chain_code =
        params.chainSelectValues.value.chain_code;

      //店舗情報を取得する
      params.shopListFromItems.value = await getShopList(params.shopListInput);
      params.shopListToItems.value = params.shopListFromItems.value;
    } catch (err: unknown) {
      console.error(err);
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = err.message;
      } else {
        params.errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }
  };

  /**
   * 出荷元選択時 @change
   */
  const onChangeShopFrom = async (): Promise<void> => {
    params.shopData_from_name.value = "";
    params.shopData_from_address.value = "";
    params.shopData_from_zip_code.value = "";
    params.deliveryTimeListInput.shop_code = "";
    params.deliveryTimeListInput.area_code = [];

    if (!params.shopSelectFromValues.value) {
      params.shopSelectFromValues.value = { shop_code: "", name: "" };
      return;
    }

    if (!params.shopSelectToValues.value) {
      params.shopSelectToValues.value = { shop_code: "", name: "" };
    }

    if (params.shopSelectFromValues.value.shop_code == "") {
      return;
    }

    // 出荷元と配達先が同じかをチェックする
    if (
      params.shopSelectToValues.value.shop_code !== "" &&
      params.shopSelectFromValues.value.shop_code ===
        params.shopSelectToValues.value.shop_code
    ) {
      params.shopSelectFromValues.value = { shop_code: "", name: "" };
      params.errdialog.value = true;
      params.errMessage.value =
        "出荷元と配達先は、異なる店舗を設定してください。";
      return;
    }

    try {
      //配達エリア取得
      await params.GetAreaData(params.shopSelectFromValues.value.shop_code);
      if (params.deliveryTimeListInput.area_code.length == 0) {
        params.shopSelectFromValues.value = { shop_code: "", name: "" };
        params.errdialog.value = true;
        params.errMessage.value = "配達エリアが存在しません。";
        return;
      }

      params.deliveryTimeListInput.shop_code =
        params.shopSelectFromValues.value.shop_code;

      // 店舗情報取得
      const shop_data = await params.getShopData(
        params.shopSelectFromValues.value.shop_code
      );

      params.shopData.value = undefined;
      params.shopData.value = shop_data!.data!.getShop!;
      params.shopData_from_name.value = params.shopData.value!.name;
      params.shopData_from_address.value =
        "〒" +
        params.shopData.value!.zip_code +
        "\r\n" +
        params.shopData.value!.address1 +
        " " +
        params.shopData.value!.address2;
      if (
        params.shopData.value!.address3 !== undefined &&
        params.shopData.value!.address3 !== null &&
        params.shopData.value!.address3 !== ""
      ) {
        params.shopData_from_address.value +=
          " " + params.shopData.value!.address3;
      }
      params.shopData_from_zip_code.value = params.shopData.value!.zip_code;

      params.shopSelectFromValues.value.name = params.shopData.value!.name;
    } catch (err: unknown) {
      console.error(err);
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = err.message;
      } else {
        params.errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }
  };

  /**
   * 配達先選択時 @change
   */
  const onChangeShopTo = async (): Promise<void> => {
    params.shopData_to_name.value = "";
    params.shopData_to_address.value = "";
    params.deliverableAreaDeliveryIdInput.delivered_zip_code = "";

    if (!params.shopSelectToValues.value) {
      params.shopSelectToValues.value = { shop_code: "", name: "" };
      return;
    }

    if (!params.shopSelectFromValues.value) {
      params.shopSelectFromValues.value = { shop_code: "", name: "" };
    }

    if (params.shopSelectToValues.value.shop_code == "") {
      return;
    }

    if (
      params.shopSelectFromValues.value.shop_code !== "" &&
      params.shopSelectToValues.value?.shop_code ===
        params.shopSelectFromValues.value.shop_code
    ) {
      params.shopSelectToValues.value = { shop_code: "", name: "" };
      params.errdialog.value = true;
      params.errMessage.value =
        "出荷元と配達先は、異なる店舗を設定してください。";
      return;
    }

    try {
      // 店舗情報取得
      const shop_data = await params.getShopData(
        params.shopSelectToValues.value.shop_code
      );
      params.shopData.value = undefined;
      params.shopData.value = shop_data!.data!.getShop!;
      params.shopData_to_name.value = params.shopData.value!.name;
      params.shopData_to_address.value =
        "〒" +
        params.shopData.value!.zip_code +
        "\r\n" +
        params.shopData.value!.address1 +
        " " +
        params.shopData.value!.address2;
      if (
        params.shopData.value!.address3 !== undefined &&
        params.shopData.value!.address3 !== null &&
        params.shopData.value!.address3 !== ""
      ) {
        params.shopData_to_address.value +=
          " " + params.shopData.value!.address3;
      }

      if (
        params.shopData.value!.phone !== undefined &&
        params.shopData.value!.phone !== null &&
        params.shopData.value!.phone !== ""
      ) {
        params.shopData_to_address.value +=
          "\r\n" + params.shopData.value!.phone!;
      }
      params.deliverableAreaDeliveryIdInput.delivered_zip_code =
        params.shopData.value!.zip_code;

      params.shopSelectToValues.value.name = params.shopData.value!.name;
    } catch (err: unknown) {
      console.error(err);
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = err.message;
      } else {
        params.errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }
  };

  return {
    onChangeChain,
    onChangeShopFrom,
    onChangeShopTo,
  };
};
